import {t, Trans} from '@lingui/macro'
import React from 'react'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'

import {Button, Inline, Link, Loading, Stack, Text, tokens} from '@pleo-io/telescope'

import {OtpType} from '@product-web/api-types/otp'
import {useCodeInput} from '@product-web/auth--code-input'
import {OTP_EXPIRATION_TIME} from '@product-web/auth--otp/api'
import {useTimer} from '@product-web/auth--otp/timer/use-timer'
import {useVerifiedLogin} from '@product-web/auth--session/context'
import {useLoginAttemptChallenge} from '@product-web/auth--session/use-login-attempt-challenge'
import {reportError} from '@product-web/error/report'
import {useAppLanguage} from '@product-web/i18n'
import {getHelpCentreArticleLink} from '@product-web/locale/helpers'
import {breakpoints} from '@product-web/styles/theme'
import {useToaster} from '@product-web/toaster'
import {useDebouncePromise} from '@product-web/use-debounce-promise'
import {LoginScreen, useLoginLocationState} from '@product-web-features/sca/login/login.helpers'
import {
    MAX_PHONE_OTP_SUBMITS_BEFORE_EXPIRATION,
    RESET_ERROR_DELAY,
    useOtpResendTimer,
} from '@product-web-features/sca/sca'

import {bff} from '../../../bff-hooks'
import Screen from '../../../components/auth-flow-screen-template'
import authenticatorOtpImage from '../../../images/authenticator-otp.svg'
import ExpiredIllustration from '../../../images/otp-expired.svg'
import smsOtpImage from '../../../images/sms-otp.svg'
import {DEBOUNCE_OTP_REQUEST_MS} from '../../../libs/debounce-otp-request'
import {isWellKnownScaError} from '../../../libs/errors'
import * as s from '../../../libs/otp.styles'
import {
    isRequestOtpSuccessResponse,
    useRequestOtpErrorHandler,
} from '../../../libs/use-request-otp-error-handler'

export function OtpValidationForEmailVerificationScreenV2() {
    const {
        state: {
            email,
            loginToken,
            passcode,
            phoneLastDigits,
            googleAuth,
            redirect,
            otp: previousOtp,
        },
        update,
    } = useLoginLocationState()

    const otpType = phoneLastDigits ? OtpType.PHONE : OtpType.GOOGLE_AUTH

    const {codeChallenge, codeChallengeMethod} = useLoginAttemptChallenge()
    const [isSubmittingOtp, setIsSubmittingOtp] = React.useState(false)
    const [isResendingToken, setIsResendingToken] = React.useState(false)
    const [isInvalidOtp, setIsInvalidOtp] = React.useState(false)
    const [isTooManyAttempts, setIsTooManyAttempts] = React.useState(false)
    const [blockedTime, setBlockedTime] = React.useState(0)
    const {showToast} = useToaster()
    const verifiedLogin = useVerifiedLogin()
    const navigate = useNavigate()
    const appLanguage = useAppLanguage()
    const [submitOtpCount, setSubmitOtpCount] = React.useState(0)

    const {mutateAsync: requestOtp} = bff.userAccountManagement.login.requestOtp.useMutation()

    const requestOtpDebounced = useDebouncePromise(requestOtp, DEBOUNCE_OTP_REQUEST_MS, {
        leading: true,
    })

    React.useEffect(() => {
        if (isInvalidOtp) {
            const invalidOtpTimer = setTimeout(() => {
                setIsInvalidOtp(false)
                reset()
            }, RESET_ERROR_DELAY)

            return () => clearTimeout(invalidOtpTimer)
        }

        return undefined
    }, [isInvalidOtp])

    const blockedTimer = useTimer(googleAuth ? blockedTime : 0)
    const accountLockedTimerHours = blockedTimer.hours === '00' ? '' : `${blockedTimer.hours}:`
    const accountLockedTimer = `${accountLockedTimerHours}${blockedTimer.minutes}:${blockedTimer.seconds}`

    React.useEffect(() => {
        if (blockedTimer.currentTime <= 0) {
            setBlockedTime(0)
            setIsTooManyAttempts(false)
        }
    }, [blockedTimer.currentTime])

    // When the token is sent we begin a countdown timer.
    // Each token is valid for 10 minutes.
    const timer = useTimer(!googleAuth ? OTP_EXPIRATION_TIME : 0)
    const resendTimer = useOtpResendTimer()

    const isTokenVerifying = timer.currentTime !== 0

    const isTokenExpired =
        !googleAuth &&
        (timer.currentTime === 0 || submitOtpCount === MAX_PHONE_OTP_SUBMITS_BEFORE_EXPIRATION)

    const navigateBack = () => {
        if (loginToken) {
            // Resetting this takes use to the email screen
            // At this point we'll have the email prefilled.
            update({screen: LoginScreen.EMAIL})
            return
        }
        // Resetting this takes the user back to the passcode screen
        update({
            screen: LoginScreen.PASSCODE,
            otp: null,
            passcode: null,
            phoneLastDigits: undefined,
            googleAuth: undefined,
        })
    }

    const handleRequestOtpError = useRequestOtpErrorHandler()

    const resendToken = async () => {
        if (!email || (!passcode && !loginToken) || isResendingToken) {
            return
        }

        try {
            // Ensure we clear the formerly expired code here on resend
            update({otp: null})
            setIsResendingToken(true)
            setIsInvalidOtp(false)
            resendTimer.handleDisableResend()

            const response = await requestOtpDebounced({
                email,
                loginToken,
                passcode,
                language: appLanguage,
            })
            if (isRequestOtpSuccessResponse(response)) {
                update(response)
                timer.reset()
                setSubmitOtpCount(0)
            } else {
                handleRequestOtpError(response, onGenericOtpRequestError)
            }
        } catch (err) {
            reportError(err)
            onGenericOtpRequestError()
        } finally {
            setIsResendingToken(false)
        }
    }

    const onGenericOtpRequestError = () => {
        showToast(t`Please try again, and if the problem continues, contact support.`, {
            level: 'error',
            title: t`Can't resend verification code`,
            isDismissable: true,
        })
    }

    const onSubmitOtp = async (otp: string) => {
        if (
            !email ||
            !otp ||
            !otpType ||
            !codeChallenge ||
            !codeChallengeMethod ||
            (!passcode && !loginToken)
        ) {
            return
        }

        try {
            setIsInvalidOtp(false)
            setIsTooManyAttempts(false)
            setIsSubmittingOtp(true)

            const response = await verifiedLogin({
                email,
                loginToken: loginToken ?? null,
                passcode: passcode ?? null,
                otp,
                trust: false,
                otpType,
                codeChallenge,
                codeChallengeMethod,
            })

            if (response.result === 'authenticated') {
                navigate('/trust-browser', {
                    state: {
                        addAccount: false,
                        redirectTo: redirect,
                    },
                })
            } else {
                update({
                    otp,
                    otpType,
                    screen: LoginScreen.EMAIL_VALIDATION,
                })
            }
        } catch (err) {
            handleSubmitOtpError((err as any)?.response?.data)
        } finally {
            setSubmitOtpCount(submitOtpCount + 1)
            setIsSubmittingOtp(false)
        }
    }

    const {element, reset} = useCodeInput({
        initialValue: previousOtp,
        onSubmit: onSubmitOtp,
        hasError: isInvalidOtp || isTooManyAttempts,
    })

    const onGenericOtpSubmitError = (error: unknown) => {
        showToast(t`Please try again, and if the problem continues, contact support.`, {
            level: 'error',
            title: t`Can't submit verification code`,
            isDismissable: true,
        })

        // error will only be reported if it's not request error
        reportError(error, 'OTP submit error was not handled properly')
    }

    const handleSubmitOtpError = (
        response: unknown,
        fallback: (response: unknown) => void = onGenericOtpSubmitError,
    ) => {
        if (!isWellKnownScaError(response)) {
            fallback(response)
            return
        }

        const {message, wait} = response

        if (
            message === 'Invalid credentials' ||
            message === 'phone_otp_does_not_match' ||
            message === 'google_auth_does_not_match'
        ) {
            setIsInvalidOtp(true)
        } else if (message === 'throttled' && wait) {
            setIsTooManyAttempts(true)
            setBlockedTime(wait)
        } else {
            fallback(response)
        }
    }

    const isLoading = isResendingToken || isSubmittingOtp

    const image = isTokenExpired
        ? ExpiredIllustration
        : googleAuth
          ? authenticatorOtpImage
          : smsOtpImage

    return (
        <Screen
            imgSrc={image}
            hideImageOnSmallScreen={!isTokenExpired}
            hideLogoOnSmallScreen={false}
            hideBackButton={isTokenExpired}
            onBack={navigateBack}
        >
            <Wrapper>
                <Stack space={40} stretch>
                    {!isTokenExpired && (
                        <>
                            <Stack space={16} stretch>
                                <Text variant="3xlarge-accent">
                                    <Trans>Enter verification code</Trans>
                                </Text>
                                <Text variant="medium-default">
                                    {!googleAuth && (
                                        <InlineText>
                                            <Trans>
                                                Check your phone and enter the 6-digit code we've
                                                sent to your number ending in {phoneLastDigits}.
                                            </Trans>{' '}
                                            <Text
                                                variant="medium-default"
                                                color={'colorContentInteractiveDisabled'}
                                            >
                                                {isSubmittingOtp ? (
                                                    <Trans>New number?</Trans>
                                                ) : (
                                                    <Link
                                                        href={getHelpCentreArticleLink(
                                                            '103000284452-reset-authentication-method',
                                                        )}
                                                        target="_blank"
                                                    >
                                                        <Trans>New number?</Trans>
                                                    </Link>
                                                )}
                                            </Text>
                                        </InlineText>
                                    )}
                                    {googleAuth && (
                                        <>
                                            <Trans>
                                                Check your authenticator app and enter the 6-digit
                                                code you see there.
                                            </Trans>{' '}
                                            <Link
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href=" https://support.google.com/accounts/answer/1066447?hl=en&ref_topic=2954345"
                                            >
                                                <Trans>Learn more</Trans>
                                            </Link>
                                        </>
                                    )}
                                </Text>
                            </Stack>
                            <Stack space={16} stretch>
                                {isLoading ? (
                                    <LoadingWrapper alignX="center" alignY="center">
                                        <Loading />
                                    </LoadingWrapper>
                                ) : (
                                    element
                                )}

                                {isInvalidOtp && (
                                    <Text variant="medium-default" color="red700">
                                        <Trans>Incorrect verification code, try again.</Trans>
                                    </Text>
                                )}
                                {isTooManyAttempts && (
                                    <Text
                                        variant="medium-default"
                                        color="red700"
                                        data-testid="too-many-attempts"
                                    >
                                        <Trans>
                                            Too many failed attempts. Your account has been locked,
                                            try again in {accountLockedTimer}.
                                        </Trans>
                                    </Text>
                                )}
                                {!googleAuth && !isLoading && (
                                    <Inline space={18} alignY="center" alignX="left">
                                        <Text variant="medium-default">
                                            <Trans>Code expires in</Trans>{' '}
                                            {isTokenVerifying && (
                                                <s.TimerFragmentWrapper>
                                                    {timer.minutes}:{timer.seconds}
                                                </s.TimerFragmentWrapper>
                                            )}
                                        </Text>

                                        <Button
                                            variant="tertiary"
                                            onClick={resendToken}
                                            disabled={resendTimer.isResendDisabled}
                                        >
                                            <Trans>Resend</Trans>
                                        </Button>
                                    </Inline>
                                )}
                            </Stack>
                        </>
                    )}
                    {isTokenExpired && (
                        <>
                            <Stack space={16} stretch>
                                <Text variant="3xlarge-accent">
                                    <Trans>Code expired</Trans>
                                </Text>
                                <Text variant="medium-default">
                                    <Trans>
                                        The code we've sent to your phone number ending in{' '}
                                        {phoneLastDigits} expired, get a new one to log in.
                                    </Trans>{' '}
                                    <Link
                                        href={getHelpCentreArticleLink(
                                            '103000284452-reset-authentication-method',
                                        )}
                                        target="_blank"
                                    >
                                        <Trans>New number?</Trans>
                                    </Link>
                                </Text>
                            </Stack>
                            <div>
                                <Button
                                    variant="primary"
                                    onClick={resendToken}
                                    loading={isResendingToken}
                                >
                                    <Trans>Resend code</Trans>
                                </Button>
                            </div>
                        </>
                    )}
                    {googleAuth && (
                        <Text variant="medium-default" color={'colorContentInteractiveDisabled'}>
                            {!isSubmittingOtp ? (
                                <Link
                                    href={getHelpCentreArticleLink(
                                        '103000284452-reset-authentication-method',
                                    )}
                                    target="_blank"
                                >
                                    <Trans>Lost access to your authenticator app?</Trans>
                                </Link>
                            ) : (
                                <Trans>Lost access to your authenticator app?</Trans>
                            )}
                        </Text>
                    )}
                </Stack>
            </Wrapper>
        </Screen>
    )
}

const Wrapper = styled.div`
    @media (max-width: ${breakpoints.mobileLrgUp}) {
        margin: ${tokens.spacing20};
    }
`

const InlineText = styled.div`
    display: inline;

    & > * {
        display: inline;
    }
`

const LoadingWrapper = styled(Inline)`
    height: 60px;
    width: 100%;
`
